/**
 * GateSquadsScreen — Quality Gate 3 · Squads (gate de FASE).
 *
 * Fecha a Fase 4 (Definição dos Squads): as personas (Gerente de Projetos ·
 * Scrum Master) validam a COMPOSIÇÃO do squad desenhada no Squad Designer
 * (squads + agentes + modelos, persistida em working_state.squad_design) antes
 * de liberar os Ciclos. Aprova/Rejeita via PATCH /journey-gates/squads. Lê
 * ?project=<id>. Espelha o GATE_DEFS.squads do protótipo.
 *
 * Agentes especialistas (SPEC A2 FR-17): a partir da conferência
 * (GET /projects/{id}/squad-design/conferencia) mostra, por membro, o selo do prompt,
 * o modelo efetivo, as skills sem conteúdo e o custo estimado; o mapa etapa → membro
 * (só leitura, com "Editar no Squad Designer"); e o quadro "O que não é agente".
 * O PATCH pode responder 409 (`detail` + `bloqueios`) ou 503 (conferência indisponível,
 * nunca aprovação silenciosa) — os dois aparecem aqui; o waiver auditado é a saída.
 */
import { C } from '@/theme/palette';
import { useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useProjects } from '@/api/projects';
import {
  useJourneyGate, useDecideJourneyGate, useSquadDesign, useSquadConferencia,
} from '@/api/techScope';
import { GateExportBar } from './GateExportBar';
import { SeloPrompt } from '@/screens/journey/steps/squad/SeloPrompt';
import { MapaResponsaveis } from '@/screens/journey/steps/squad/MapaResponsaveis';
import {
  usd as usdPt, custoDoModelo, seloDoMembro, TEXTO_DO_SELO,
} from '@/screens/journey/steps/squad/comum';

/** Texto fixo do quadro "O que não é agente" (AG-13, FR-17). */
export const TEXTO_O_QUE_NAO_E_AGENTE = 'Verificação (verify), runtime, contrato, gate pré-geração e '
  + 'conferência estática são determinísticos de propósito — não dependem de prompt.';

/** Membros por página no gate (NFR-3: pagina, nunca corta). */
const MEMBROS_POR_PAGINA = 30;

// Paleta: segue o tema global (aliases bg/surface/border/text/dim/muted… em theme/palette.js).

// Aprovadores/personas/SLA do Gate de Squads (espelha o protótipo · GATE_DEFS.squads).
const GATE_DEF = {
  num: 3, label: 'Squads', sla: '24h',
  personas: 'Gerente de Projetos · Scrum Master',
  approvers: [
    { name: 'Rafael Pinto', role: 'Gerente de Projetos', avatar: 'RP' },
    { name: 'Juliana Castro', role: 'Scrum Master', avatar: 'JC' },
  ],
};

const ST = {
  approved: { label: 'APROVADO', fg: C.ok, bg: 'rgba(1,230,228,0.12)' },
  rejected: { label: 'REJEITADO', fg: C.bad, bg: 'rgba(255,51,0,0.12)' },
  pending:  { label: 'AGUARDANDO APROVAÇÃO', fg: C.warn, bg: 'rgba(255,176,32,0.12)' },
};

// Estimativa de custo por membro (mesma tabela do Squad Designer e da conferência).
const usd = usdPt;
const squadCostOf = (sq) =>
  (sq?.agents || []).reduce((acc, a) => acc + custoDoModelo(a?.model), 0);

/** Erro do PATCH do gate em pt-BR: 409 com `bloqueios`, 503 sem conferência. */
function ErroDaDecisao({ error }) {
  if (!error) return null;
  const corpo = error.problemBody || {};
  const bloqueios = Array.isArray(corpo.bloqueios) ? corpo.bloqueios : [];
  let titulo;
  if (error.status === 409) titulo = 'O squad não pode ser aprovado ainda.';
  else if (error.status === 503) {
    titulo = 'A conferência do squad está indisponível agora — o gate NÃO foi aprovado. Tente de novo em instantes.';
  } else titulo = String(error.title || error.message || error);
  const bruto = corpo.detail ?? error.detail;
  const detalhe = typeof bruto === 'string' ? bruto : '';
  return (
    <div role="alert" data-testid="erro-decisao"
      style={{ marginTop: 12, padding: '10px 12px', borderRadius: 8, color: C.bad, fontSize: 13,
        background: 'rgba(255,51,0,0.08)', border: `1px solid ${C.bad}55` }}>
      <div style={{ fontWeight: 700 }}>{titulo}</div>
      {detalhe && <div style={{ marginTop: 4 }}>{detalhe}</div>}
      {bloqueios.length > 0 && (
        <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
          {bloqueios.map((b, i) => <li key={`${b.codigo}-${i}`}>{b.mensagem}</li>)}
        </ul>
      )}
    </div>
  );
}

/** Membros com selo, modelo efetivo, skills sem conteúdo e custo (FR-17). Pagina acima de 30. */
function MembrosConferidos({ membros }) {
  const [pagina, setPagina] = useState(0);
  const paginas = Math.max(1, Math.ceil(membros.length / MEMBROS_POR_PAGINA));
  const atual = Math.min(pagina, paginas - 1);
  const visiveis = membros.slice(atual * MEMBROS_POR_PAGINA, (atual + 1) * MEMBROS_POR_PAGINA);
  const th = { textAlign: 'left', padding: '6px 10px', fontSize: 10.5, color: C.muted, letterSpacing: '0.05em',
    textTransform: 'uppercase', borderBottom: `1px solid ${C.border}` };
  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }} aria-label="Membros do squad conferidos">
          <thead>
            <tr>
              <th style={th}>Membro</th><th style={th}>Prompt</th><th style={th}>Modelo</th>
              <th style={th}>Skills sem conteúdo</th><th style={th}>Custo est.</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((m) => {
              const semConteudo = (m.skills || []).filter((k) => k?.tem_conteudo === false).map((k) => k.name);
              return (
                <tr key={m.agent} data-testid={`gate-membro-${m.agent}`}>
                  <td style={{ padding: '7px 10px', fontSize: 12.5 }}>
                    <span style={{ color: C.text, fontWeight: 600 }}>{m.label || m.agent}</span>
                    <div style={{ fontSize: 10.5, color: C.muted, fontFamily: 'monospace' }}>{m.agent}</div>
                  </td>
                  <td style={{ padding: '7px 10px' }}><SeloPrompt membro={m} compacto /></td>
                  <td style={{ padding: '7px 10px', fontSize: 11.5, fontFamily: 'monospace', color: C.dim }}>
                    <span style={{ textTransform: 'uppercase' }}>{m.model_efetivo}</span>
                    <span style={{ color: C.muted }}> · {m.model_origem === 'membro' ? 'trocado no projeto' : 'do catálogo'}</span>
                  </td>
                  <td style={{ padding: '7px 10px', fontSize: 12, color: semConteudo.length ? C.warn : C.muted }}>
                    {semConteudo.length ? semConteudo.map((n) => `⚠️ ${n}`).join(' · ') : '—'}
                  </td>
                  <td style={{ padding: '7px 10px', fontSize: 12, color: C.dim, whiteSpace: 'nowrap' }}>
                    {usd(m.custo_estimado_usd ?? custoDoModelo(m.model_efetivo))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {paginas > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end', marginTop: 8, fontSize: 12, color: C.dim }}>
          <button type="button" disabled={atual === 0} onClick={() => setPagina(atual - 1)} aria-label="Membros: página anterior"
            style={{ background: 'transparent', color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, padding: '2px 8px', cursor: 'pointer' }}>‹</button>
          Página {atual + 1} de {paginas}
          <button type="button" disabled={atual >= paginas - 1} onClick={() => setPagina(atual + 1)} aria-label="Membros: próxima página"
            style={{ background: 'transparent', color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, padding: '2px 8px', cursor: 'pointer' }}>›</button>
        </div>
      )}
    </div>
  );
}

function Card({ title, right, accent = C.accent, children }) {
  return (
    <div style={{ border: `1px solid ${C.border}`, background: C.surface, borderRadius: 12, padding: 16, marginTop: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <h4 style={{ margin: 0, fontFamily: 'monospace', fontSize: 11, textTransform: 'uppercase',
          letterSpacing: '0.1em', color: accent }}>{title}</h4>
        {right && <span style={{ marginLeft: 'auto' }}>{right}</span>}
      </div>
      {children}
    </div>
  );
}

function Pill({ label, value }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '8px 14px',
      border: `1px solid ${C.border}`, borderRadius: 10, background: C.surface }}>
      <span style={{ fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.muted }}>{label}</span>
      <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{value}</span>
    </div>
  );
}

export function GateSquadsScreen({ projectId: propProjectId, onContinue, onBack } = {}) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const urlProject = searchParams.get('project');
  const { data: projectsRaw } = useProjects();
  const projects = useMemo(
    () => (Array.isArray(projectsRaw) ? projectsRaw : projectsRaw?.items ?? []),
    [projectsRaw],
  );
  const projectId = propProjectId || urlProject || projects?.[0]?.id || null;

  const { data: gate } = useJourneyGate(projectId, 'squads');
  const decide = useDecideJourneyGate(projectId, 'squads');
  const { data: designData } = useSquadDesign(projectId);
  const conferenciaQ = useSquadConferencia(projectId);
  const [comment, setComment] = useState('');
  const [waiverReason, setWaiverReason] = useState('');

  if (!projectId) return <div style={{ padding: 40, color: C.dim }}>Nenhum projeto selecionado.</div>;

  const status = gate?.status ?? 'pending';
  const s = ST[status] || ST.pending;
  const approved = status === 'approved';

  const design = designData?.design || {};
  const squads = Array.isArray(design.squads) ? design.squads : [];
  const nodes = Array.isArray(design.nodes) ? design.nodes : [];
  // Orquestração = nós agente SEM squad (Product Owner / Tech Lead).
  const orchestration = nodes.filter((n) => n?.type === 'agent' && !n?.squad);
  const squadAgentCount = squads.reduce((acc, sq) => acc + (sq?.agents?.length || 0), 0);
  const agentCount = squadAgentCount || nodes.filter((n) => n?.type === 'agent').length;
  const templatesUsed = new Set(squads.map((s) => s?.template).filter(Boolean)).size;
  const conferencia = conferenciaQ.data && typeof conferenciaQ.data === 'object' ? conferenciaQ.data : null;
  const membrosConf = Array.isArray(conferencia?.membros) ? conferencia.membros.filter((m) => m?.agent) : [];
  const bloqueiosConf = Array.isArray(conferencia?.bloqueios) ? conferencia.bloqueios : [];
  const alertasConf = Array.isArray(conferencia?.alertas) ? conferencia.alertas : [];
  const totalCost = membrosConf.length
    ? membrosConf.reduce((acc, m) => acc + (Number(m.custo_estimado_usd) || custoDoModelo(m.model_efetivo)), 0)
    : squads.reduce((acc, sq) => acc + squadCostOf(sq), 0)
      + orchestration.reduce((acc, n) => acc + custoDoModelo(n?.model), 0);
  const hasSquads = squads.length > 0;
  const erroDecisao = decide.error;
  const mostrarWaiver = !approved && (erroDecisao?.status === 409 || bloqueiosConf.length > 0);

  const goDesigner = () => navigate(`/journey/designer?project=${projectId}`);
  // "Editar no Squad Designer" (FR-17): no wizard a etapa anterior É o Squad Designer.
  const goSquadDesigner = () => (onBack ? onBack() : navigate(`/journey?project=${projectId}&step=squad`));
  // Embutido no wizard: avança para os Ciclos (Backlog) via onContinue.
  // Standalone: volta para a Gestão do Projeto.
  const goJourney = () => (onContinue ? onContinue() : navigate(`/project?project=${projectId}`));

  // ── objeto normalizado para exportação (.md / PDF) — GateExportBar ─────────
  const exportGate = {
    num: GATE_DEF.num,
    label: GATE_DEF.label,
    projectId,
    description: 'Encerramento da Definição dos Squads — as personas validam a composição '
      + 'desenhada no Squad Designer (squads, agentes e modelos) antes de liberar os Ciclos.',
    status,
    statusLabel: s.label,
    decidedAt: gate?.decidedAt,
    comment: gate?.comment,
    sla: GATE_DEF.sla,
    personas: GATE_DEF.personas,
    approvers: GATE_DEF.approvers.map((a) => ({ ...a, done: approved })),
    sections: [
      orchestration.length ? {
        title: 'Orquestração',
        kind: 'list',
        items: orchestration.map((n) => `${n.label || n.agent} — modelo ${n.model || 'sonnet'}`),
      } : null,
      hasSquads ? {
        title: 'Composição do squad (Squad Designer)',
        kind: 'text',
        text: squads.map((sq) => {
          const header = `- **${sq.name || 'Squad'}**${sq.template ? ` (${sq.template})` : ''} — `
            + `${(sq.agents || []).length} agentes · ${usd(squadCostOf(sq))} estimado`;
          const agentLines = (sq.agents || [])
            .map((ag) => `  - ${ag.label || ag.role} — ${ag.model}`)
            .join('\n');
          return agentLines ? `${header}\n${agentLines}` : header;
        }).join('\n'),
      } : null,
      membrosConf.length ? {
        title: 'Prompts de especialista (conferência)',
        kind: 'list',
        items: membrosConf.map((m) => `${m.label || m.agent} (${m.agent}) — `
          + `${TEXTO_DO_SELO[seloDoMembro(m)] ?? m.selo} · ${m.model_efetivo} · ${usd(m.custo_estimado_usd)}`),
      } : null,
      (conferencia?.etapas || []).length ? {
        title: 'Mapa etapa → membro',
        kind: 'list',
        items: conferencia.etapas.filter((e) => e?.aplicavel !== false).map((e) =>
          `${e.rotulo || e.etapa}: ${e.origem === 'aprovado' ? e.responsavel : `${e.proposta ?? '—'} (${e.origem ?? 'sem dono'})`}`),
      } : null,
    ].filter(Boolean),
  };

  return (
    <div className="sp-page" style={{ color: C.text, maxWidth: 980 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 22, letterSpacing: '-0.02em' }}>
          🛡 Quality Gate {GATE_DEF.num} — {GATE_DEF.label}
        </h2>
        <span style={{ fontFamily: 'monospace', fontSize: 11, padding: '3px 9px', borderRadius: 6,
          color: s.fg, background: s.bg, letterSpacing: '0.04em' }}>{s.label}</span>
        <GateExportBar gate={exportGate} style={{ marginLeft: 'auto' }} />
      </div>
      <p style={{ color: C.dim, marginTop: 8, fontSize: 13.5, maxWidth: '80ch' }}>
        Encerramento da Definição dos Squads — as personas validam a composição desenhada no
        Squad Designer (squads, agentes e modelos) antes de liberar os Ciclos. A composição
        é salva no projeto e alimenta a execução (organize_squad honra o que foi definido).
      </p>

      {/* Pills de métricas */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
        <Pill label="Squads" value={String(squads.length || '—')} />
        <Pill label="Agentes"
          value={agentCount ? `${agentCount}${orchestration.length ? ` + ${orchestration.length} orquestração` : ''}` : '—'} />
        <Pill label="Custo estimado" value={totalCost ? usd(totalCost) : '—'} />
        <Pill label="Templates" value={`${templatesUsed} de 4 usados`} />
      </div>

      {gate?.decidedAt && (
        <p style={{ color: C.muted, fontSize: 12.5, marginTop: 12 }}>
          Decidido em {new Date(gate.decidedAt).toLocaleString('pt-BR')}
          {gate.comment ? ` · ${gate.comment}` : ''}
        </p>
      )}
      {decide.isError && <ErroDaDecisao error={erroDecisao} />}

      {/* Orquestração (Product Owner · Tech Lead) */}
      {orchestration.length > 0 && (
        <Card title="Orquestração" accent={C.quantum}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {orchestration.map((n) => (
              <div key={n.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                background: C.black, borderRadius: 8, border: `1px solid ${C.border}` }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: C.quantum, flexShrink: 0 }} />
                <span style={{ fontSize: 12.5, color: C.text, fontWeight: 600 }}>{n.label || n.agent}</span>
                <span style={{ fontSize: 10, color: C.muted, fontFamily: 'monospace', textTransform: 'uppercase' }}>{n.model || 'sonnet'}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Composição do squad */}
      <Card title="Composição do squad (Squad Designer)"
        right={<button onClick={goDesigner} style={{ cursor: 'pointer', background: 'transparent',
          color: C.accent, border: `1px solid ${C.accent}55`, borderRadius: 7, padding: '5px 11px',
          fontSize: 12 }}>👁 Abrir no Squad Designer</button>}>
        {hasSquads ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
            {squads.map((sq) => (
              <div key={sq.id} style={{ padding: 12, background: C.black, borderRadius: 8,
                border: `1px solid ${C.border}`, borderLeft: `3px solid ${sq.color || C.accent}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: C.text, flex: 1 }}>{sq.name || 'Squad'}</span>
                  {sq.template && (
                    <span style={{ fontSize: 9, fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.06em',
                      color: sq.color || C.accent, border: `1px solid ${(sq.color || C.accent)}55`, borderRadius: 5, padding: '1px 6px' }}>
                      {sq.template}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 10.5, color: C.muted, marginBottom: 8 }}>
                  {(sq.agents || []).length} agentes · {usd(squadCostOf(sq))} estimado
                </div>
                {sq.mission && <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.4, marginBottom: 8 }}>{sq.mission}</div>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {(sq.agents || []).map((ag) => (
                    <div key={ag.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: sq.color || C.accent, flexShrink: 0 }} />
                      <span style={{ fontSize: 12, color: C.dim, flex: 1 }}>{ag.label || ag.role}</span>
                      <span style={{ fontSize: 10, color: C.muted, fontFamily: 'monospace', textTransform: 'uppercase' }}>{ag.model}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 12.5, color: C.muted }}>
            Nenhuma composição salva ainda — volte ao Squad Designer, monte os squads e salve antes de aprovar o gate.
          </div>
        )}
      </Card>

      {/* Prompts de especialista por membro (FR-17) */}
      <Card title="Membros e prompts de especialista" accent={C.accent}
        right={conferencia?.contract_mode && (
          <span style={{ fontSize: 11, fontFamily: 'monospace', color: conferencia.contract_mode === 'strict' ? C.warn : C.muted }}>
            {conferencia.contract_mode === 'strict' ? 'modo estrito' : 'modo híbrido'}
          </span>
        )}>
        {conferenciaQ.isLoading ? (
          <div style={{ fontSize: 12.5, color: C.muted }}>Conferindo o squad…</div>
        ) : conferenciaQ.isError ? (
          <div style={{ fontSize: 12.5, color: C.warn }}>
            Não foi possível conferir o squad agora. A aprovação continua conferida pela API — nada é aprovado sem conferência.
          </div>
        ) : membrosConf.length ? (
          <MembrosConferidos membros={membrosConf} />
        ) : (
          <div style={{ fontSize: 12.5, color: C.muted }}>Nenhum membro conferido — salve a composição no Squad Designer.</div>
        )}
        {alertasConf.length > 0 && (
          <ul style={{ margin: '10px 0 0', paddingLeft: 18, fontSize: 12, color: C.warn }}>
            {alertasConf.map((a, i) => <li key={`${a.codigo}-${a.agent ?? a.etapa ?? i}`}>{a.mensagem}</li>)}
          </ul>
        )}
      </Card>

      {/* Mapa etapa → membro, só leitura (FR-17) */}
      <Card title="Mapa etapa → membro" accent={C.quantum}
        right={<button type="button" onClick={goSquadDesigner} style={{ cursor: 'pointer', background: 'transparent',
          color: C.accent, border: `1px solid ${C.accent}55`, borderRadius: 7, padding: '5px 11px',
          fontSize: 12 }}>Editar no Squad Designer</button>}>
        <MapaResponsaveis etapas={conferencia?.etapas || []}
          membros={membrosConf.map((m) => ({ agent: m.agent, label: m.label, tipo: m.tipo }))} somenteLeitura />
        {bloqueiosConf.length > 0 && !approved && (
          <div data-testid="bloqueios-previstos" style={{ marginTop: 10, fontSize: 12.5, color: C.warn }}>
            <strong>Antes de aprovar:</strong>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {bloqueiosConf.map((b, i) => <li key={`${b.codigo}-${i}`}>{b.mensagem}</li>)}
            </ul>
          </div>
        )}
      </Card>

      {/* O que não é agente (AG-13) */}
      <Card title="O que não é agente" accent={C.muted}>
        <div style={{ fontSize: 12.5, color: C.dim, lineHeight: 1.5 }}>{TEXTO_O_QUE_NAO_E_AGENTE}</div>
      </Card>

      {/* Painel de aprovadores + SLA + parecer */}
      <div style={{ marginTop: 20, border: `1px solid ${C.border}`, background: C.surface2, borderRadius: 12, padding: 16 }}>
        {approved && (
          <div style={{ marginBottom: 14, padding: '10px 12px', borderRadius: 8,
            background: 'rgba(1,230,228,0.06)', border: `1px solid ${C.accent}44`, fontSize: 12.5, color: C.dim }}>
            <strong style={{ color: C.accent }}>Gate aprovado — Definição dos Squads concluída.</strong> Aprovações
            registradas na trilha de auditoria imutável (hash-chain).
          </div>
        )}
        <div style={{ fontSize: 10, letterSpacing: '0.05em', color: C.muted, fontWeight: 700, marginBottom: 8 }}>
          APROVADORES · {GATE_DEF.personas.toUpperCase()}
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          {GATE_DEF.approvers.map((a) => (
            <div key={a.name} style={{ display: 'flex', alignItems: 'center', gap: 8,
              border: `1px solid ${C.border}`, borderRadius: 999, padding: '4px 12px 4px 4px', background: C.black }}>
              <span style={{ width: 26, height: 26, borderRadius: 999, background: approved ? C.accent : C.surface2,
                color: approved ? C.black : C.dim, display: 'grid', placeItems: 'center',
                fontSize: 10, fontWeight: 700 }}>{a.avatar}</span>
              <span style={{ fontSize: 12 }}>
                <span style={{ color: C.text, fontWeight: 600 }}>{a.name}</span>
                <span style={{ color: C.muted }}> · {a.role}</span>
              </span>
              {approved && <span style={{ color: C.ok, fontSize: 12 }}>✓</span>}
            </div>
          ))}
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
          ⏱ SLA: aprovação em até {GATE_DEF.sla} · Escala para backup em 48h · Admin em 72h
        </div>

        <label style={{ fontSize: 12.5, color: C.dim }}>Parecer do aprovador (obrigatório para rejeitar)</label>
        <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2}
          placeholder="Ex: o squad de qualidade precisa de um security-auditor antes do Ciclo 0…"
          style={{ width: '100%', marginTop: 6, background: C.black, color: C.text,
            border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, fontSize: 13, resize: 'vertical' }} />

        <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
          {onBack && (
            <button onClick={onBack} style={{ background: 'transparent', color: C.dim,
              border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 16px', fontSize: 13,
              fontWeight: 600, cursor: 'pointer' }}>
              ← Voltar
            </button>
          )}
          {approved ? (
            <button onClick={goJourney} style={{ background: C.accent, color: C.black, border: 'none',
              borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              Continuar jornada →
            </button>
          ) : (
            <>
              <button onClick={() => decide.mutate({ decision: 'approve', comment })} disabled={decide.isPending}
                style={{ background: C.accent, color: C.black, border: 'none', borderRadius: 8,
                  padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: decide.isPending ? 0.6 : 1 }}>
                {decide.isPending ? 'Registrando…' : 'Aprovar Definição dos Squads'}
              </button>
              <button onClick={() => decide.mutate({ decision: 'reject', comment })} disabled={decide.isPending}
                style={{ background: 'transparent', color: C.bad, border: `1px solid ${C.bad}`, borderRadius: 8,
                  padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Rejeitar</button>
            </>
          )}
        </div>

        {/* Waiver auditado — a saída governada para os bloqueios (FR-19, DA-6) */}
        {mostrarWaiver && (
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
            <label htmlFor="squads-waiver" style={{ fontSize: 12.5, color: C.dim }}>
              Justificativa do waiver (risco aceito — obrigatória e auditada)
            </label>
            <textarea id="squads-waiver" value={waiverReason} onChange={(e) => setWaiverReason(e.target.value)} rows={2}
              placeholder="Ex: o membro X roda sem prompt de especialista neste ciclo porque…"
              style={{ width: '100%', marginTop: 6, background: C.black, color: C.text,
                border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, fontSize: 13, resize: 'vertical' }} />
            <button type="button" disabled={decide.isPending || !waiverReason.trim()}
              onClick={() => decide.mutate({ decision: 'waiver', comment, waiver_reason: waiverReason.trim() })}
              style={{ marginTop: 8, background: 'transparent', color: C.warn, border: `1px solid ${C.warn}`, borderRadius: 8,
                padding: '8px 14px', fontSize: 13, fontWeight: 600,
                cursor: decide.isPending || !waiverReason.trim() ? 'not-allowed' : 'pointer',
                opacity: decide.isPending || !waiverReason.trim() ? 0.5 : 1 }}>
              Aprovar com waiver
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
